// 投票總人數／總可被投票數報表整頁（vote-total-vote-report＝/tsghrpt/vote/totalVote、
// vote-total-vote-analysis-report＝/tsghrpt/vote/totalVoteAnalysis）。批次 21（Gitea issue #24）。
// 掛在 PageBehavior.reportBody（批次 14/18 先例；兩頁 JSP/JS 僅 title/端點差異——
// totalVoteReport.jsp vs totalVoteAnalysisReport.jsp diff 4 行（title :6/breadcrumb2 :19/href2 :20/
// h5 :27/js :13）；totalVoteReport.js vs totalVoteAnalysisReport.js diff 1 行（doExport URL :70）。
//
// legacy 流程（行號親驗 2026-09-27；基準＝views/vote/totalVoteReport.jsp(85)＋
// js-my/vote/totalVoteReport.js(73)＋ExcellentTeacherVoteReportController.java:38-49；詳
// docs/plans/2026-09-27-batch21-vote-pages-plan.md §1.3）：
//   - ready（js:5-8）：bindEvents＋`$("select[name=organizationId]").val(loginAccountOrganizationId)
//     .trigger("change")`(:7)（layout server 變數＝登入帳號所屬醫院；雛形種子預設第一個選項 org-1）。
//   - annualId/organizationId change(:12-19)→setVoteList(:32-41)：**legacy bug 照實（D21-5）：
//     `$("select[name=orgId]")` 不存在（JSP name=organizationId）＝orgId undefined**→voteId select
//     empty＋填 vote/list 列（v.id/v.name）；getVoteList(:45-70)＝GET vote/list
//     {annualId, orgId, enableTypes:['ENABLE']}（**async:false** 同步，D6 原生 select 鏡像）。
//   - 匯出（js:20-29）：voteId undefined/null→showAlertDialog('請選擇投票！')(:25)；doExport(:69-73)
//     ＝window.open(`tsghrpt/vote/exportTotalVoteReport?voteId=${voteId}`,'_blank').focus()——
//     雛形以 runReportExport（list:<pageId>#export ✓ endpoint-map＋雛形不產生檔案 notice）鏡像（D21-7，
//     批次 14/18 先例；window.open 不開新視窗）。
//   - JSP（totalVoteReport.jsp）：title 投票總人數(:6)；breadcrumb1 報表管理(:18)；*年度 annualId
//     （annualList getChineseYear 民國 :36-42）／*醫院 organizationId(:47-53)／*投票名稱 voteId
//     （空，JS 填 :61-66）／匯出結果 #exportBtn(:73)。
//
// 【證據等級】§7 第 3 級（原始碼）。種子【雛形值待實機校準】。

import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { ProcessingOverlay, useDialogs } from '@/components/layouts';
import { LegacyContractModals } from '@/components/layouts/LegacyContractModals';
import { ContentPanel } from '@/components/layouts';
import {
  ACCOUNT_ORGANIZATION_OPTIONS,
  COURSE_CLAZZ_ANNUAL_OPTIONS,
} from '@/mocks/resource-rules';
import { MockDataError, useDataSource } from '@/mocks';
import { selectOptionsOverride } from '@/mocks/legacy-fields';
import type { Row } from '@/mocks/types';
import { runReportExport, ReportRow } from '@/pages/report/ReportKit';

/** 訊息字面值（totalVoteReport.js；！＝FF01）。 */
const TEXT_NO_VOTE = '請選擇投票！'; // js:25
const TEXT_QUERY_ERROR = '查詢失敗，請聯絡系統管理員！'; // js:63 error handler

/** 兩報表頁的設定差異（title/h5/breadcrumb2/doExport URL 文字＝僅此 4+1 處不同）。 */
export interface VoteTotalReportConfig {
  readonly title: string; // jsp:6/:27（<title> 與 <h5> 同文）
  readonly exportUrl: string; // js:70：tsghrpt/vote/exportTotalVoteReport｜exportTotalVoteAnalysisReport
}

/** 投票名稱下拉選項（vote/list 列 → {value: v.id, text: v.name}，js:35-39）。 */
export function toVoteOptions(rows: readonly Row[]) {
  return rows.map((row) => ({ value: String(row['id'] ?? ''), label: String(row['name'] ?? '') }));
}

export function VoteTotalReportBody(props: {
  readonly pageId: string;
  readonly config: VoteTotalReportConfig;
}): ReactElement {
  const { pageId, config } = props;
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const [annualId, setAnnualId] = useState(COURSE_CLAZZ_ANNUAL_OPTIONS[0]?.value ?? '');
  // 初始＝loginAccountOrganizationId（js:9；雛形種子預設第一個選項 org-1【雛形值待實機校準】）。
  const [organizationId, setOrganizationId] = useState(ACCOUNT_ORGANIZATION_OPTIONS[0]?.value ?? '');
  const [voteId, setVoteId] = useState('');
  const [voteOptions, setVoteOptions] = useState<readonly { value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);

  /**
   * setVoteList（js:32-41）：**legacy bug 照實（D21-5）**——org 讀 select[name=orgId] 不存在＝
   * orgId undefined→vote/list 只 by 年度（＋enableTypes，D21-4 mock 層不帶）。
   */
  const setVoteList = useCallback(
    async (currentAnnualId: string) => {
      try {
        const result = await dataSource.list(pageId, { filters: { annualId: currentAnnualId } });
        setVoteOptions(toVoteOptions(result.rows));
        setVoteId(''); // $voteId.empty()（js:33）後重填＝選擇重置
      } catch (cause: unknown) {
        if (cause instanceof MockDataError && cause.message === '查詢失敗') {
          await dialogs.alert(cause.message);
        } else {
          await dialogs.alert(TEXT_QUERY_ERROR);
        }
      }
    },
    // dataSource/dialogs 為 hooks 常數。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageId],
  );

  // ready（js:5-8）：org 預設觸發 change→setVoteList。
  useEffect(() => {
    void (async () => {
      await setVoteList(COURSE_CLAZZ_ANNUAL_OPTIONS[0]?.value ?? '');
      setLoading(false);
    })();
    // 初始載入一次。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 匯出（js:20-29）：未選投票→alert；doExport→runReportExport（D21-7 window.open 鏡像）。
   *  filters 直送三欄（C5-h 接線語意，批次 18 同款；D21-7）；legacy 匯出 URL 僅帶 voteId（js:70）——
   *  年度/醫院值不進 legacy URL＝D21-7 登記（mock 層 filters 直送不影響 legacy 等價）。 */
  const doExport = async (): Promise<void> => {
    if (voteId === '') {
      // legacy：typeof voteId == 'undefined' || voteId == null（js:23-26）；空 select 選不到＝等價。
      await dialogs.alert(TEXT_NO_VOTE);
      return;
    }
    await runReportExport({
      pageId,
      filters: { annualId, organizationId, voteId },
      dataSource,
      dialogs,
    });
  };

  if (loading) {
    return <ProcessingOverlay uiId={`${pageId}-loading`} open />;
  }

  return (
    <div data-ui-id={pageId}>
      <LegacyContractModals pageId={pageId} />
      <ContentPanel uiId={`${pageId}-panel`} title={config.title}>
        <div data-ui-id={`${pageId}-query`} className="mb-4 grid grid-cols-2 gap-x-4 gap-y-2">
          <ReportRow pageId={pageId} name="annualId" label="年度：" star>
            <Select
              uiId={`${pageId}-field-annual-id`}
              name="annualId"
              value={annualId}
              // 批次 52 修訂（b52-calibrate3，F-2）：override 命中＝凍結選項集取代寫死常數（規格 §7.1）。
              options={selectOptionsOverride(pageId, 'annualId') ?? COURSE_CLAZZ_ANNUAL_OPTIONS}
              onChange={(next) => {
                setAnnualId(next);
                void setVoteList(next); // annualId change→setVoteList（js:12-15）
              }}
            />
          </ReportRow>
          <ReportRow pageId={pageId} name="organizationId" label="醫院：" star>
            <Select
              uiId={`${pageId}-field-organization-id`}
              name="organizationId"
              value={organizationId}
              // 批次 52 修訂（b52-calibrate3，F-2）：override 命中＝凍結選項集取代寫死常數（規格 §7.1）。
              options={selectOptionsOverride(pageId, 'organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS}
              onChange={(next) => {
                setOrganizationId(next);
                void setVoteList(annualId); // organizationId change→setVoteList（js:16-19；D21-5 orgId 不帶）
              }}
            />
          </ReportRow>
          <ReportRow pageId={pageId} name="voteId" label="投票名稱：" star>
            <Select
              uiId={`${pageId}-field-vote-id`}
              name="voteId"
              value={voteId}
              // 批次 52 種子值校準：voteId 凍結真空（OPTION_SET_OVERRIDES=[]）＝0 投票選項（js:33 $voteId.empty 後無資料）；override 未命中照舊 list 池。
              options={selectOptionsOverride(pageId, 'voteId') ?? voteOptions}
              onChange={setVoteId}
            />
          </ReportRow>
        </div>
        <div data-ui-id={`${pageId}-actions`} className="mb-3 flex gap-2">
          {/* 匯出結果（jsp:73 #exportBtn；dataEffect＝list:<pageId>#export，endpoint-map ✓）。 */}
          <Button
            uiId={`${pageId}-export-btn`}
            variant="primary"
            size="sm"
            type="button"
            dataEffect={`list:${pageId}#export`}
            onClick={() => void doExport()}
          >
            匯出結果
          </Button>
        </div>
      </ContentPanel>
    </div>
  );
}

export function TotalVoteReportBody({ pageId }: { pageId: string }): ReactElement {
  // totalVoteReport.jsp:6/:27 `<title>投票總人數`／js:70 exportTotalVoteReport。
  return <VoteTotalReportBody pageId={pageId} config={{ title: '投票總人數', exportUrl: 'tsghrpt/vote/exportTotalVoteReport' }} />;
}

export function TotalVoteAnalysisReportBody({ pageId }: { pageId: string }): ReactElement {
  // totalVoteAnalysisReport.jsp:6/:27 `<title>總可被投票數`／js:70 exportTotalVoteAnalysisReport。
  return (
    <VoteTotalReportBody
      pageId={pageId}
      config={{ title: '總可被投票數', exportUrl: 'tsghrpt/vote/exportTotalVoteAnalysisReport' }}
    />
  );
}
